import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * TosAcceptanceGate
 *
 * Global, pre-render gate for the whole authenticated app. `auth.user.
 * requiresTosAcceptance` (computed server-side by AuthService.getCurrentUser,
 * see backend/src/auth/auth.service.ts) is true only for a user whose account
 * was created after the ToS gate's launch cutoff and who has not yet
 * accepted: in practice, an admin-invited GROUP teammate at their own first
 * login. Self-registration already requires acceptance at signup time, so
 * this almost never fires for anyone else, and never fires at all for a
 * pre-existing account (createdAt predates the cutoff by construction).
 *
 * Mounted above the whole route tree (in App.js, wrapping <Routes>), not
 * scoped to /company/* the way GroupApprovalGate is: an admin-invited
 * teammate can reach /negotiations/:matchId and /notifications outside
 * /company/*, and the gate has to catch those too. It resolves before
 * GroupApprovalGate (which sits deeper, inside the /company/* route), so a
 * GROUP teammate who needs both gates always sees ToS acceptance first.
 *
 * Only /accept-terms itself is exempt, to avoid an infinite redirect loop.
 * Public/unauthenticated routes are unaffected: requiresTosAcceptance is
 * only ever true when auth.user exists.
 */
const ALLOWED_PATHS = ['/accept-terms'];

const TosAcceptanceGate = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const requiresAcceptance = auth?.user?.requiresTosAcceptance === true;
  const pathAllowed = ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!requiresAcceptance || pathAllowed) return;
    // Carry the originally-requested destination so AcceptTerms.jsx can
    // send the user there once they accept, instead of always falling back
    // to their role's generic default landing page.
    navigate('/accept-terms', {
      replace: true,
      state: { from: location.pathname + location.search },
    });
  }, [requiresAcceptance, pathAllowed, navigate, location.pathname, location.search]);

  // While a gated user is on a blocked path, render nothing so the
  // underlying page never mounts and never fires API calls. Once the
  // redirect lands on /accept-terms, pathAllowed flips true and children
  // render (the interstitial itself).
  if (requiresAcceptance && !pathAllowed) {
    return null;
  }

  return children;
};

export default TosAcceptanceGate;
